/**
 * Cookie Consent Banner with Preferences
 *
 * PECR/GDPR-compliant cookie consent with category toggles.
 * Stores preferences in localStorage.
 *
 * TODO: Analytics preference is stored but not yet enforced.
 * When Google Analytics is added, check useCookiePreferences().analytics
 * before loading GA scripts. Example:
 *
 *   const prefs = useCookiePreferences()
 *   if (prefs?.analytics) {
 *     // Load Google Analytics
 *   }
 */

import { useState, useEffect } from 'react'

const CONSENT_KEY = 'cookie_preferences'

// Approximate height of the cookie banner for layout spacing
export const COOKIE_BANNER_HEIGHT = 120

export interface CookiePreferences {
  essential: boolean // Always true, can't be disabled
  analytics: boolean
}

const DEFAULT_PREFERENCES: CookiePreferences = {
  essential: true,
  analytics: false,
}

export const useCookiePreferences = (): CookiePreferences | null => {
  const [preferences, setPreferences] = useState<CookiePreferences | null>(null)

  useEffect(() => {
    try {
      const stored = localStorage.getItem(CONSENT_KEY)
      if (stored) {
        setPreferences(JSON.parse(stored))
      }
    } catch {
      // localStorage unavailable or invalid JSON
    }
  }, [])

  return preferences
}

/**
 * Hook to check if cookie consent banner should be visible
 * Returns true if no consent has been given yet
 */
export const useCookieConsentVisible = (): boolean => {
  const [isVisible, setIsVisible] = useState(false)

  useEffect(() => {
    try {
      const stored = localStorage.getItem(CONSENT_KEY)
      setIsVisible(!stored)
    } catch {
      setIsVisible(false)
    }
  }, [])

  return isVisible
}

export const CookieConsent = () => {
  const [isVisible, setIsVisible] = useState(false)
  const [showPreferences, setShowPreferences] = useState(false)
  const [preferences, setPreferences] = useState<CookiePreferences>(DEFAULT_PREFERENCES)

  useEffect(() => {
    try {
      const stored = localStorage.getItem(CONSENT_KEY)
      if (!stored) {
        setIsVisible(true)
      }
    } catch {
      // localStorage unavailable
    }
  }, [])

  const savePreferences = (prefs: CookiePreferences) => {
    try {
      localStorage.setItem(CONSENT_KEY, JSON.stringify(prefs))
    } catch {
      // localStorage unavailable
    }
    setIsVisible(false)
  }

  const handleAcceptAll = () => {
    savePreferences({ essential: true, analytics: true })
  }

  const handleRejectAll = () => {
    savePreferences({ essential: true, analytics: false })
  }

  const handleSavePreferences = () => {
    savePreferences(preferences)
  }

  if (!isVisible) return null

  return (
    <>
      {/* Spacer to prevent banner from covering footer */}
      <div style={{ height: `${COOKIE_BANNER_HEIGHT}px` }} />

      {/* Fixed banner */}
      <div
        style={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          background: '#2c3e50',
          color: 'white',
          padding: '1.5rem 2rem',
          zIndex: 1000,
          boxShadow: '0 -2px 10px rgba(0,0,0,0.2)',
        }}
      >
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          {!showPreferences ? (
            // Main banner
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '1.5rem',
                flexWrap: 'wrap',
              }}
            >
              <div style={{ flex: 1, minWidth: '280px' }}>
                <p style={{ margin: '0 0 0.5rem 0', fontSize: '0.95rem' }}>
                  We use cookies to improve your experience and analyse how the service is used.
                </p>
                <p style={{ margin: 0, fontSize: '0.85rem', opacity: 0.8 }}>
                  This tool uses AI which may produce errors - please review results.{' '}
                  <a href="/terms" style={{ color: '#3498db' }}>
                    Terms
                  </a>
                </p>
              </div>
              <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                <button
                  onClick={() => setShowPreferences(true)}
                  style={{
                    background: 'transparent',
                    border: '1px solid #95a5a6',
                    color: '#ecf0f1',
                    padding: '0.5rem 1rem',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '0.9rem',
                  }}
                >
                  Manage preferences
                </button>
                <button
                  onClick={handleRejectAll}
                  style={{
                    background: 'transparent',
                    border: '1px solid #95a5a6',
                    color: '#ecf0f1',
                    padding: '0.5rem 1rem',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '0.9rem',
                  }}
                >
                  Reject all
                </button>
                <button
                  onClick={handleAcceptAll}
                  style={{
                    background: '#3498db',
                    border: 'none',
                    color: 'white',
                    padding: '0.5rem 1rem',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '0.9rem',
                  }}
                >
                  Accept all
                </button>
              </div>
            </div>
          ) : (
            // Preferences panel
            <div>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '1rem',
                }}
              >
                <h3 style={{ margin: 0, fontSize: '1.1rem' }}>Cookie preferences</h3>
                <button
                  onClick={() => setShowPreferences(false)}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#95a5a6',
                    cursor: 'pointer',
                    fontSize: '1.2rem',
                  }}
                  aria-label="Close preferences"
                >
                  ×
                </button>
              </div>

              <div style={{ marginBottom: '1rem' }}>
                {/* Essential cookies */}
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '0.75rem 0',
                    borderBottom: '1px solid #34495e',
                  }}
                >
                  <div>
                    <strong style={{ fontSize: '0.95rem' }}>Essential</strong>
                    <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.85rem', opacity: 0.8 }}>
                      Required for the service to function (authentication, security)
                    </p>
                  </div>
                  <div
                    style={{
                      background: '#27ae60',
                      color: 'white',
                      padding: '0.25rem 0.75rem',
                      borderRadius: '4px',
                      fontSize: '0.8rem',
                    }}
                  >
                    Always on
                  </div>
                </div>

                {/* Analytics cookies */}
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '0.75rem 0',
                  }}
                >
                  <div>
                    <strong style={{ fontSize: '0.95rem' }}>Analytics</strong>
                    <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.85rem', opacity: 0.8 }}>
                      Help us understand how the service is used (Google Analytics)
                    </p>
                  </div>
                  <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={preferences.analytics}
                      onChange={(e) =>
                        setPreferences({ ...preferences, analytics: e.target.checked })
                      }
                      style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                    />
                  </label>
                </div>
              </div>

              <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                <button
                  onClick={handleRejectAll}
                  style={{
                    background: 'transparent',
                    border: '1px solid #95a5a6',
                    color: '#ecf0f1',
                    padding: '0.5rem 1rem',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '0.9rem',
                  }}
                >
                  Reject all
                </button>
                <button
                  onClick={handleSavePreferences}
                  style={{
                    background: '#3498db',
                    border: 'none',
                    color: 'white',
                    padding: '0.5rem 1rem',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '0.9rem',
                  }}
                >
                  Save preferences
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  )
}
